// vim: tabstop=8 softtabstop=0 noexpandtab shiftwidth=8 nosmarttab

import { LitElement, html, css, PropertyValues } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { ConfirmDialog } from './confirm-dialog.js';

interface MenuOption {
	label: string;
	action: string;
	isDialog?: boolean;
	confirmation?: string;
	callback?: () => Promise<boolean>;
}

declare global {
	interface HTMLElementEventMap {
		'menu-action': CustomEvent<{ action: string }>;
		'show-dialog': CustomEvent<{ dialogType: string; action: string }>;
		'hide-dialog': CustomEvent<void>;
	}
}

@customElement('first-boot-menu')
export class FirstBootMenu extends LitElement {
	@property({ type: String })
	version = '1.0.0';

	@property({ attribute: 'menu-title', type: String })
	menuTitle = 'Kuromi';

	@property({ attribute: 'menu-subtitle', type: String })
	menuSubtitle = 'Development';

	@property({ type: String })
	copyright = 'Digital Signage Bunny © 2025. All rights reserved.';

	@property({ type: Array })
	menuOptions: MenuOption[] = [
		{
			label: 'Register Device',
			action: 'register-device',
		},
		{
			label: 'Reload Application',
			action: 'reload-application',
			confirmation: 'Are you sure you want to reload the application?',
			callback: async () => {
				// Implement the reload application functionality
				window.location.reload();
				return true;
			}
		},
	];

	@query('confirm-dialog')
	private confirmDialog!: ConfirmDialog;

	@state()
	isVisible = false;

	@state()
	private selectedIndex = 0;

	@state()
	private showDialog = false;

	@state()
	private currentDialogAction = '';

	static override styles = css`\
:host {
	position: fixed;
	top: 0;
	left: 0;
	width: 100vw;
	height: 100vh;
	background: linear-gradient(135deg, #0f1419 0%, #1a2332 50%, #2d3748 100%);
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: flex-start;
	padding: 0;
	font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
	font-size: 12px;
	color: #ffffff;
	opacity: 0;
	visibility: hidden;
	transition: all 0.3s ease-in-out;
	z-index: 1000;
}

:host([visible]) {
	opacity: 1;
	visibility: visible;
}

.menu-container {
	position: relative;
	width: 100%;
	max-width: 600px;
	padding: 0 10em;
}

.title {
	font-size: 6em;
	font-weight: 300;
	letter-spacing: 0.2em;
	margin-top: 1em;
	margin-bottom: 0;
	text-transform: uppercase;
	background: linear-gradient(45deg, #00d4ff, #7c3aed);
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
	background-clip: text;
	text-shadow: 0 0 20px rgba(0, 212, 255, 0.3);
}

.subtitle {
	font-size: 1.8em;
	font-weight: 600;
	letter-spacing: 0.1em;
	margin-bottom: 4em;
	color: #fff;
	position: relative;
}

.subtitle-background {
	background: linear-gradient(90deg, #ffa500, transparent 50%);
	position: absolute;
	width: 100%;
	height: 100%;
	z-index: -1;
	padding: 0.2em;
	top: -0.1em;
	left: -0.4em;
	border-radius: 8px;
}

.menu-list {
	list-style: none;
	padding: 0;
	margin: 0;
}

.menu-item {
	height: 60px;
	font-size: 2em;
	font-weight: 400;
	cursor: pointer;
	transition: all 0.3s ease;
	position: relative;
	display: flex;
	align-items: center;
	color: #ccc;
	text-shadow: 2px 2px 4px rgba(0, 0, 0, 1);
}

.menu-item.selected {
	color: #fff;
}

.menu-item.selected:hover {
	color: #fff;
}

.bunny-icon {
	position: absolute;
	left: -36px;
	top: 20px;
	width: 24px;
	height: 24px;
	filter: drop-shadow(2px 2px 4px rgba(0, 0, 0, 1));
	opacity: 0;
	transition: opacity 0.3s ease;
}

.menu-item.selected .bunny-icon {
	opacity: 1;
}

.menu-item-text {
	margin-left: 0;
	transition: margin-left 0.3s ease;
}

.menu-item.selected .menu-item-text {
	font-size: 1.5em;
}

.footer {
	position: absolute;
	bottom: 2em;
	left: 0;
	right: 0;
	display: flex;
	justify-content: center;
	font-size: 0.9em;
	color: #a0a0a0;
	font-weight: 300;
}

.version {
	position: absolute;
	right: 4em;
}

.background-overlay {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background:
		radial-gradient(circle at 20% 80%, rgba(124, 58, 237, 0.1) 0%, transparent 50%),
		radial-gradient(circle at 80% 20%, rgba(0, 212, 255, 0.1) 0%, transparent 50%);
	pointer-events: none;
}

.dialog-container {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	display: flex;
	align-items: flex-start;
	justify-content: flex-start;
	z-index: 10;
}

.dialog-container ::slotted(*) {
	/* Ensure slotted dialog content appears above the menu */
	position: relative;
	z-index: 11;
	/* Remove any conflicting backgrounds from slotted elements */
	background: transparent !important;
}

@keyframes fadeIn {
	from {
		opacity: 0;
		transform: translateY(20px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

:host([visible]) .menu-item {
	animation: fadeIn 0.5s ease forwards;
	opacity: 0;
}

:host([visible]) .menu-item:nth-child(1) { animation-delay: 0.1s; }
:host([visible]) .menu-item:nth-child(2) { animation-delay: 0.15s; }
:host([visible]) .menu-item:nth-child(3) { animation-delay: 0.2s; }
:host([visible]) .menu-item:nth-child(4) { animation-delay: 0.25s; }
:host([visible]) .menu-item:nth-child(5) { animation-delay: 0.3s; }
:host([visible]) .menu-item:nth-child(6) { animation-delay: 0.35s; }
:host([visible]) .menu-item:nth-child(7) { animation-delay: 0.4s; }
:host([visible]) .menu-item:nth-child(8) { animation-delay: 0.45s; }
:host([visible]) .menu-item:nth-child(9) { animation-delay: 0.5s; }`;

	private _onKeyDown = this.onKeyDown.bind(this);
	private _isKeyDownListening = false;

	private addKeyDownListener(): void {
		if(!this._isKeyDownListening) {
			console.log('Adding keydown listener');
			this._isKeyDownListening = true;
			document.addEventListener('keydown', this._onKeyDown);
		}
	}

	private removeKeyDownListener(): void {
		if(this._isKeyDownListening) {
			console.log('Removing keydown listener');
			this._isKeyDownListening = false;
			document.removeEventListener('keydown', this._onKeyDown);
		}
	}

	override connectedCallback(): void {
		super.connectedCallback();
	}

	override disconnectedCallback(): void {
		super.disconnectedCallback();
		this.removeKeyDownListener();
	}

	private resetMenu(): void {
		this.selectedIndex = 0;
		this.showDialog = false;
		this.currentDialogAction = '';
	}

	private onKeyDown(event: KeyboardEvent): void {
		// If dialog is shown, let it handle its own keyboard events
		if(this.showDialog) {
			return;
		}

		if(!this.isVisible) return;

		switch(event.key) {
		case 'ArrowUp':
			event.preventDefault();
			this.selectedIndex = this.selectedIndex > 0
				? this.selectedIndex - 1
				: this.menuOptions.length - 1;
			break;

		case 'ArrowDown':
			event.preventDefault();
			this.selectedIndex = this.selectedIndex < this.menuOptions.length - 1
				? this.selectedIndex + 1
				: 0;
			break;

		case 'Enter':
			event.preventDefault();
			this.executeSelectedOption();
			break;

		default: break;
		}
	}

	showMenu(): void {
		this.isVisible = true;
		this.resetMenu();
		this.addKeyDownListener();
	}

	hideMenu(): void {
		this.isVisible = false;
		this.removeKeyDownListener();
	}

	private async executeSelectedOption(): Promise<void> {
		const selectedOption = this.menuOptions[this.selectedIndex];
		if(!selectedOption) return;

		if(selectedOption.action) {
			if(selectedOption.confirmation) {
				const confirmation = await ConfirmDialog.confirm(
					this.confirmDialog,
					selectedOption.confirmation,
				);
				if(!confirmation) {
					return;
				}
			}
			// Check if this action should show a dialog
			if(selectedOption.isDialog) {
				this.showDialogForAction(selectedOption.action);
				return;
			}

			// Dispatch action event for non-dialog actions
			this.onMenuAction(selectedOption.action);
		}
	}

	private showDialogForAction(action: string): void {
		this.currentDialogAction = action;
		this.showDialog = true;

		this.removeKeyDownListener();

		// Dispatch event to notify that a dialog should be shown
		this.dispatchEvent(new CustomEvent('show-dialog', {
			detail: {
				dialogType: action,
				action: action
			},
			bubbles: true,
			composed: true
		}));
	}

	private hideDialog(): void {
		this.showDialog = false;
		this.currentDialogAction = '';
	}

	private onHideDialog = (): void => {
		this.hideDialog();
		this.addKeyDownListener();
	};

	private onMenuAction(action: string): void {
		this.removeKeyDownListener();

		// Dispatch custom event for parent components to handle
		this.dispatchEvent(new CustomEvent('menu-action', {
			detail: { action },
			bubbles: true,
			composed: true
		}));

		// Close menu after `register-device` action only.
		if(action === 'register-device') {
			this.isVisible = false;
		}
	}

	private onMenuItemClick(index: number): void {
		this.selectedIndex = index;
		this.executeSelectedOption();
	}

	private onMenuItemHover(index: number): void {
		this.selectedIndex = index;
	}

	private getBunnyIcon(): any {
		return html`\
<svg class="bunny-icon" xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 -960 960 960" width="24" fill="#e8eaed">
	<path d="M380-80q-75 0-127.5-52.5T200-260q0-35 17-64.5t63-75.5q6-6 11.5-12.5T306-430q-51-78-78.5-163.5T200-760q0-58 21-89t59-31q57 0 102 55t68 101q9 20 16.5 40.5T480-641q6-22 13.5-42.5T511-724q22-46 67-101t102-55q38 0 59 31t21 89q0 81-27.5 166.5T654-430q9 11 14.5 17.5T680-400q46 46 63 75.5t17 64.5q0 75-52.5 127.5T580-80q-45 0-72.5-10L480-100l-27.5 10Q425-80 380-80Zm0-80q23 0 46-5.5t43-16.5q-11-5-20-17t-9-21q0-8 11.5-14t28.5-6q17 0 28.5 6t11.5 14q0 9-9 21t-20 17q20 11 43 16.5t46 5.5q42 0 71-29t29-71q0-18-10-35t-30-34q-14-12-23-21t-29-34q-29-35-48-45.5T480-440q-41 0-60.5 10.5T372-384q-20 25-29 34t-23 21q-20 17-30 34t-10 35q0 42 29 71t71 29Zm40-130q-8 0-14-9t-6-21q0-12 6-21t14-9q8 0 14 9t6 21q0 12-6 21t-14 9Zm120 0q-8 0-14-9t-6-21q0-12 6-21t14-9q8 0 14 9t6 21q0 12-6 21t-14 9ZM363-489q11-8 25-14t31-11q-2-48-14.5-95.5T373-696q-19-40-42-67.5T285-799q-2 6-3.5 15.5T280-760q0 68 21.5 138T363-489Zm234 0q40-63 61.5-133T680-760q0-14-1.5-23.5T675-799q-23 8-46 35.5T587-696q-18 39-30.5 86.5T541-514q15 4 29 10.5t27 14.5Z"/>
</svg>`;
	}

	override updated(changedProperties: PropertyValues): void {
		if(changedProperties.has('isVisible')) {
			this.toggleAttribute('visible', this.isVisible);
		}

		// Reinitialize menu when options change
		if(changedProperties.has('menuOptions')) {
			this.resetMenu();
		}
	}

	override render() {
		return html`
<div class="container">
	<div class="background-overlay"></div>
	${this.showDialog ? html`\
<div class="dialog-container">
	<slot @hide-dialog=${this.onHideDialog}></slot>
</div>` : html`\
<div class="menu-container">
	<h1 class="title">${this.menuTitle}</h1>
	${this.menuSubtitle ? html`<div class="subtitle"><div class="subtitle-background"></div>${this.menuSubtitle}</div>` : ''}
	<ul class="menu-list">
		${this.menuOptions.map((option, index) => html`
			<li
				class="menu-item ${index === this.selectedIndex ? 'selected' : ''}"
				@click=${() => this.onMenuItemClick(index)}
				@mouseenter=${() => this.onMenuItemHover(index)}
			>
				${index === this.selectedIndex ? this.getBunnyIcon() : ''}
				<span class="menu-item-text">${option.label}</span>
			</li>
		`)}
	</ul>
</div>`}
	${this.currentDialogAction !== 'settings' ? html`\
<div class="footer">
	<div class="copyright">${this.copyright}</div>
	${this.showDialog ? '' : html`<div class="version">${this.version}</div>`}
</div>`: ''}
</div><confirm-dialog></confirm-dialog>`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'first-boot-menu': FirstBootMenu;
	}
}
